import { faAngleRight } from '@fortawesome/pro-solid-svg-icons';
import type { CSSProperties } from 'react';
import { Suspense } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { ErrorBoundary } from '@sorare/error-boundary';

import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { BrandM, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import {
  BLOG_SPORT,
  BLOG_SPORT_SLUG,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { range } from '@sorare/core/src/lib/arrays';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isNoInternetConnectionError } from '@sorare/core/src/lib/http/isNoInternetConnectionError';
import { sportToBlogSportMap } from '@sorare/core/src/lib/routing';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { useLatestPosts } from '@sorare/blog/src/hooks/useLatestPosts';

const Root = styled(Vertical)`
  padding: 0 var(--double-unit) var(--triple-unit);

  @media ${laptopAndAbove} {
    padding: 0;
  }
`;
const StyledLinkBox = styled(LinkBox)`
  transition: opacity 200ms ease-in-out;
  &:hover {
    opacity: 0.7;
  }
`;
const ImgWrapper = styled.div`
  width: 120px;
  aspect-ratio: 120/67;
  border-radius: var(--unit);
  background-image: var(--bg);
  background-size: 100%;
  background-position: center;
  flex-shrink: 0;

  transition: background-size 200ms ease-in-out;
`;
const Post = styled(Horizontal)`
  ${LabelM} {
    text-decoration: underline transparent;
    transition: text-decoration-color 300ms ease-in-out;
  }

  &:hover {
    ${LabelM} {
      text-decoration-color: var(--c-white);
    }

    ${ImgWrapper} {
      background-size: 104%;
    }
  }
`;

const Title = ({ sportSlug }: { sportSlug: Nullable<string> }) => {
  const to = sportSlug
    ? generatePath(BLOG_SPORT, {
        sport: sportSlug,
      })
    : '';
  return (
    <StyledLinkBox>
      <SBHorizontal>
        <LinkOverlay as={Link} to={to}>
          <BrandM>
            <FormattedMessage {...glossary.latestNews} />
          </BrandM>
        </LinkOverlay>
        <IconButton color="tertiary" size="small" icon={faAngleRight} to={to} />
      </SBHorizontal>
    </StyledLinkBox>
  );
};

export const Fallback = () => {
  return (
    <Root gap={1.5}>
      <Title sportSlug={null} />
      {range(3).map(i => (
        <Horizontal gap={1.5} key={i}>
          <Skeleton style={{ minWidth: '120px', height: '67px' }} />
          <Skeleton style={{ width: '100%', height: '20px' }} />
        </Horizontal>
      ))}
    </Root>
  );
};

const LatestNewsWidgetComponent = () => {
  const { sport } = useSportContext();

  const { posts } = useLatestPosts(3);

  const sportSlug = sportToBlogSportMap[sport];

  return (
    <Root gap={1.5}>
      <Title sportSlug={sportSlug} />
      <Vertical gap={1.5}>
        {posts.filter(Boolean).map(post => (
          <Post
            key={post.slug}
            gap={1.5}
            as={ButtonBase}
            to={generatePath(BLOG_SPORT_SLUG, {
              slug: post.slug,
              sport: sportSlug || 'all',
            })}
          >
            {!!post.featuredImage?.node && (
              <ImgWrapper
                style={
                  {
                    '--bg': `url(${post.featuredImage.node.thumbnail || ''})`,
                  } as CSSProperties
                }
              />
            )}
            <Vertical gap={0.5}>
              {!!post.date && (
                <LabelS color="var(--c-nd-600)">
                  <FormattedDate
                    value={post.date}
                    month="short"
                    day="numeric"
                  />
                </LabelS>
              )}
              <LabelM bold>{post.title}</LabelM>
            </Vertical>
          </Post>
        ))}
      </Vertical>
    </Root>
  );
};

export const LatestNewsWidget = () => {
  return (
    <ErrorBoundary
      fallback={null}
      doNotReportToSentry={isNoInternetConnectionError}
    >
      <Suspense fallback={<Fallback />}>
        <LatestNewsWidgetComponent />
      </Suspense>
    </ErrorBoundary>
  );
};
